import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import CurrencyInput from './CurrencyInput';

const ACCOUNT_TYPES = ['Checking', 'Savings', 'Money Market', 'CD', 'Other'];
const EMPTY_ENTRY = {
  institution: '',
  type: '',
  account_last_digits: '',
  value: null,
  interest_rate: null,
  maturity_date: '',
};

/**
 * Section 5 — Cash &amp; Savings Accounts.
 *
 * Auto-grows: a fresh empty row appears when the last visible row gets
 * any data. Mirrors the cashflow income/debt list pattern.
 */
export default function StepAccounts({ data, onChange }) {
  const value = data || {};
  const baseEntries = (value.entries && value.entries.length > 0)
    ? value.entries
    : [{ ...EMPTY_ENTRY }];

  const lastHasData = Object.values(baseEntries[baseEntries.length - 1] || {})
    .some((v) => v !== null && v !== undefined && v !== '');

  const entries = lastHasData && baseEntries.length < 10
    ? [...baseEntries, { ...EMPTY_ENTRY }]
    : baseEntries;

  function updateEntry(idx, field, val) {
    const next = entries.map((e, i) => (i === idx ? { ...e, [field]: val } : e));
    onChange({ ...value, entries: next });
  }

  function removeEntry(idx) {
    const next = entries.filter((_, i) => i !== idx);
    onChange({ ...value, entries: next.length > 0 ? next : [{ ...EMPTY_ENTRY }] });
  }

  return (
    <div>
      <SectionHeader
        number={5}
        title="Cash &amp; Savings Accounts"
        tip="List your checking, savings, money market, and CD accounts.
          You can leave some fields blank if you don't have all the
          information handy."
        showAnnualNote={false}
      />

      {entries.map((entry, idx) => {
        const isCD = entry.type === 'CD';
        return (
          <div key={idx} className="cashflow-section-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="cashflow-subsection-title mb-0">
                Account #{idx + 1}
              </h6>
              {entries.length > 1 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm text-muted p-0"
                  onClick={() => removeEntry(idx)}
                  title="Remove this account"
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label">Institution</label>
                <input
                  type="text"
                  className="form-control"
                  value={entry.institution || ''}
                  onChange={(e) => updateEntry(idx, 'institution', e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={entry.type || ''}
                  onChange={(e) => updateEntry(idx, 'type', e.target.value)}
                >
                  <option value="">Select…</option>
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Acct # (Last 3–4 Characters)</label>
                <input
                  type="text"
                  className="form-control"
                  value={entry.account_last_digits || ''}
                  onChange={(e) => updateEntry(idx, 'account_last_digits', e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <CurrencyInput
                  label="Current / average value"
                  id={`acct_value_${idx}`}
                  value={entry.value}
                  onChange={(v) => updateEntry(idx, 'value', v)}
                  noToggle
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Interest rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={entry.interest_rate ?? ''}
                  onChange={(e) =>
                    updateEntry(
                      idx,
                      'interest_rate',
                      e.target.value === '' ? null : parseFloat(e.target.value),
                    )
                  }
                />
              </div>
              {isCD && (
                <div className="col-md-4">
                  <label className="form-label">Maturity date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={entry.maturity_date || ''}
                    onChange={(e) => updateEntry(idx, 'maturity_date', e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      <NotesInput
        value={value.notes}
        onChange={(v) => onChange({ ...value, notes: v })}
      />
    </div>
  );
}
